/**
 * MX-Auth Staff Login -- the UPSTREAM Connect widget (mxauth-06, GH #1807, ADR-013 #1772).
 *
 * The entry counterpart to StaffLaunch.tsx (the terminal leg). An invited FI
 * staffer arrives here from their Aaim staff invitation link carrying the FI's
 * Quiltt institution id (?inst=<quilttInstitutionId>), their email (?email=),
 * and -- the first time -- the invitation token (?token=<invitationToken>). The
 * page drives the institutional-realm analog of the consumer fi-handoff:
 *
 *   1. POST consumer-api /api/v1/mx-auth/staff/session {quilttInstitutionId,email}
 *      -> an AUTH-ONLY, institution-scoped Quiltt Connect session
 *      {sessionToken, connectorId?}.
 *   2. Render Quiltt Connect (QuilttProvider token={sessionToken}); the staffer
 *      logs into their FI's online banking. onExitSuccess yields a connectionId.
 *   3. POST consumer-api /api/v1/mx-auth/staff/connect-result
 *      {quilttInstitutionId,email,connectionId,invitationToken?}. consumer-api
 *      reads the Quiltt connection SERVER-SIDE (anti-spoof: server-read
 *      institution id == the scoped quilttInstitutionId), then mints the staff
 *      session via aaim-core and returns MxAuthStaffAuthResponse.handoffCode.
 *   4. Redirect /launch?code=<handoffCode> (StaffLaunch consumes it -> cookie),
 *      or /launch?error=<code>&message=<...> on failure.
 *
 * The bearer never touches admin-portal JS -- only the opaque, single-use
 * handoffCode crosses, consumed once by the terminal leg. The two consumer-api
 * endpoints are unauthenticated (the staffer has no Aaim session yet) and CSRF
 * is not applicable (no cookie). no-browser-token-storage.md /
 * cookie-session-auth-canon.md.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QuilttButton, QuilttProvider } from '@quiltt/react';
import type { ConnectorSDKCallbackMetadata } from '@quiltt/react';
import { Alert, Heading, BodyText, Card, Input, Button } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/generated/mx-auth-staff';

type MxAuthStaffSessionResponse = components['schemas']['MxAuthStaffSessionResponse'];
type MxAuthStaffAuthResponse = components['schemas']['MxAuthStaffAuthResponse'];
type MxAuthError = components['schemas']['MxAuthError'];

// Cross-origin base for consumer-api (app.aaim.com), baked at build time. The
// admin.aaim.com LB routes /api/v1/* to platform-admin-api (NOT consumer-api),
// so the mx-auth/staff endpoints are reached cross-origin, never same-origin.
const CONSUMER_API = import.meta.env.VITE_CONSUMER_API_URL ?? '';

export function StaffLoginPage() {
  const [searchParams] = useSearchParams();
  const quilttInstitutionId = searchParams.get('inst') ?? '';
  // FI display name for Quiltt Connect institution pre-fill (?instName=). Quiltt's
  // `institution` prop is a SEARCH TERM / institution NAME ("Wells Fargo"), NOT the
  // inst_* id -- it prefills the institution search so the staffer lands on their FI
  // instead of searching. Captured alongside the inst_* id at IdP/invitation setup
  // and carried on the acceptance link. Absent -> the prop is omitted (plain search).
  const institutionName = searchParams.get('instName') ?? undefined;
  const invitationToken = searchParams.get('token') ?? undefined;
  const [email, setEmail] = useState(searchParams.get('email') ?? '');

  // Inline UI state-machine union (no named ad-hoc type per the OpenAPI-first gate).
  const [status, setStatus] = useState<
    'enter_email' | 'starting_session' | 'connect_ready' | 'recording' | 'redirecting' | 'error'
  >('enter_email');
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<MxAuthStaffSessionResponse | null>(null);

  // No institution scope -> the staffer did not arrive via a valid invitation link.
  useEffect(() => {
    if (!quilttInstitutionId) {
      setError(
        'This staff sign-in link is missing its institution context. Open the link from your Aaim staff invitation email.',
      );
      setStatus('error');
    }
  }, [quilttInstitutionId]);

  const redirectToLaunch = (query: string) => {
    window.location.assign(`/launch?${query}`);
  };

  // Step 1: open the auth-only, institution-scoped Quiltt Connect session.
  const startSession = async () => {
    const trimmed = email.trim();
    if (!trimmed || !quilttInstitutionId) return;
    setStatus('starting_session');
    setError(null);
    try {
      const res = await fetch(`${CONSUMER_API}/api/v1/mx-auth/staff/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quilttInstitutionId, email: trimmed }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as MxAuthError | null;
        setError(body?.message ?? 'Could not start the secure sign-in session. Please try again.');
        setStatus('error');
        return;
      }
      const data = (await res.json()) as MxAuthStaffSessionResponse;
      setSession(data);
      setStatus('connect_ready');
    } catch {
      setError('Could not reach the sign-in service. Please try again.');
      setStatus('error');
    }
  };

  // Step 3: record the Quiltt connection -> mint the staff session -> handoff.
  const onConnectSuccess = async (metadata: ConnectorSDKCallbackMetadata) => {
    const connectionId = metadata.connectionId;
    if (!connectionId) {
      redirectToLaunch(
        'error=NO_CONNECTION&message=' + encodeURIComponent('Your bank connection did not complete.'),
      );
      return;
    }
    setStatus('recording');
    try {
      const res = await fetch(`${CONSUMER_API}/api/v1/mx-auth/staff/connect-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quilttInstitutionId,
          email: email.trim(),
          connectionId,
          ...(invitationToken ? { invitationToken } : {}),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as MxAuthError | null;
        redirectToLaunch(
          `error=${encodeURIComponent(body?.error ?? 'CONNECT_RESULT_FAILED')}` +
            `&message=${encodeURIComponent(body?.message ?? 'Staff sign-in could not be completed.')}`,
        );
        return;
      }
      const data = (await res.json()) as MxAuthStaffAuthResponse;
      // handoffCode is required on the response (the session travels via the exchange
      // leg, never in this body); guard defensively against a missing/empty value.
      if (!data.handoffCode) {
        redirectToLaunch(
          'error=NO_HANDOFF_CODE&message=' +
            encodeURIComponent('Staff sign-in did not return a handoff code. Please try again.'),
        );
        return;
      }
      setStatus('redirecting');
      redirectToLaunch('code=' + encodeURIComponent(data.handoffCode));
    } catch {
      redirectToLaunch(
        'error=NETWORK&message=' + encodeURIComponent('Could not reach the sign-in service.'),
      );
    }
  };

  const onConnectFailure = () => {
    redirectToLaunch(
      'error=CONNECT_ABORTED&message=' + encodeURIComponent('Bank sign-in was cancelled or failed.'),
    );
  };

  const getProgressMessage = () => {
    switch (status) {
      case 'starting_session':
        return 'Preparing your secure institution sign-in...';
      case 'recording':
        return 'Verifying your institution and staff invitation...';
      case 'redirecting':
        return 'Completing sign-in...';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mt-16 flex flex-col items-center">
        <Card className="w-full text-center">
          <Heading level={2} size="section" className="font-sans mb-4">
            Aaim Administration Portal
          </Heading>

          {status === 'error' && (
            <>
              <Alert type="error" className="mb-6">
                {error}
              </Alert>
              {/* GH #1962: pre-authentication, so no in-app case can be filed --
                  but "your Aaim administrator" was also the wrong party. Staff
                  sign-in is configured per institution, so the person who can
                  actually act is an admin at THEIRS. */}
              <BodyText className="text-tertiary text-sm">
                If this persists, an administrator at your institution can check its
                staff sign-in configuration.
              </BodyText>
            </>
          )}

          {status === 'enter_email' && (
            <div className="text-left">
              <BodyText className="text-secondary mb-6 text-center">
                Sign in to your institution&apos;s admin portal by logging into your
                institution&apos;s online banking.
              </BodyText>
              <Input
                label="Your staff email"
                type="email"
                placeholder="you@yourinstitution.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <div className="mt-6 flex justify-center">
                <Button variant="primary" onClick={() => void startSession()} disabled={!email.trim()}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {(status === 'starting_session' || status === 'recording' || status === 'redirecting') && (
            <>
              <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-6" />
              <BodyText className="text-secondary">{getProgressMessage()}</BodyText>
            </>
          )}

          {status === 'connect_ready' && session && (
            <div className="flex flex-col items-center">
              <BodyText className="text-secondary mb-6">
                Log into your institution&apos;s online banking to confirm your staff
                membership. Aaim never sees your banking credentials.
              </BodyText>
              {session.connectorId ? (
                // connectorId is optional on the session response; Quiltt Connect
                // needs it. Mirror the consumer flow, which only renders Connect
                // when its connector id is present.
                <QuilttProvider token={session.sessionToken}>
                  <QuilttButton
                    connectorId={session.connectorId}
                    // Pre-fill the institution SEARCH so the staffer lands on their FI
                    // instead of searching. Quiltt's `institution` prop is a SEARCH
                    // TERM / institution NAME ("Wells Fargo"), NOT the inst_* id
                    // (https://www.quiltt.dev/connector/sdk/react -- "Prefilled
                    // Institution Search"). The FI's searchable name is captured at
                    // IdP/invitation setup and carried on the acceptance link
                    // (?instName=). Omitted when absent so a search is never prefilled
                    // with a value that matches nothing (the prior bug passed the
                    // inst_* id here). GH #1809 / ADR-013 (#1772).
                    institution={institutionName}
                    onExitSuccess={(metadata: ConnectorSDKCallbackMetadata) => void onConnectSuccess(metadata)}
                    onExitError={onConnectFailure}
                    onExitAbort={onConnectFailure}
                    className="bg-accent text-white px-6 py-3 rounded font-sans"
                  >
                    Sign in with your institution
                  </QuilttButton>
                </QuilttProvider>
              ) : (
                <Alert type="error">
                  Your institution is not yet configured for staff sign-in (no
                  Connect connector). An administrator at your institution can
                  request it from the Aaim portal.
                </Alert>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
